import {
  fetchAdminAdmins,
  fetchAdminPlatformInsights,
  fetchAdminSparksRedemptions,
  type AdminPlatformInsights,
  type AdminSparksRedemption,
} from "@/api/admin";
import { AdminCard, MetricCard } from "@/components/admin/AdminShell";
import { ADMIN_NOT_IMPLEMENTED, formatCoins, downloadCsv } from "@/components/admin/adminConstants";
import { Button } from "@/components/ui/Button";
import { useEffect, useState } from "react";

function StatGrid({ items }: { items: { label: string; value: string | number }[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {items.map((item) => (
        <div key={item.label} className="rounded-xl bg-[#f8f9fb] p-3">
          <div className="text-lg font-bold tabular-nums">
            {typeof item.value === "number" ? item.value.toLocaleString() : item.value}
          </div>
          <div className="text-xs text-ink-muted">{item.label}</div>
        </div>
      ))}
    </div>
  );
}

export function AdminPlatformTab() {
  const [insights, setInsights] = useState<AdminPlatformInsights | null>(null);
  const [pendingSparks, setPendingSparks] = useState<AdminSparksRedemption[]>([]);
  const [admins, setAdmins] = useState<{ username: string | null; email: string | null }[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setInsights(await fetchAdminPlatformInsights());
        setPendingSparks(await fetchAdminSparksRedemptions("pending", 25));
        setAdmins(await fetchAdminAdmins());
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load platform data");
      }
    })();
  }, []);

  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }
  if (!insights) {
    return <p className="text-sm text-ink-muted">Loading platform analytics…</p>;
  }

  const s = insights.sparks ?? {};
  const ch = insights.challenges ?? {};
  const cr = insights.creator_platform ?? {};
  const pu = insights.push ?? {};
  const pa = insights.parallel ?? {};
  const gr = insights.growth ?? {};
  const ad = insights.admob ?? {};

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Sparks redemptions"
          value={(s.redemptions_total ?? 0).toLocaleString()}
          sub={`${s.redemptions_pending ?? 0} pending · ${formatCoins(s.sparks_spent_total)} spent`}
        />
        <MetricCard
          label="Challenge entries"
          value={(ch.entries_total ?? 0).toLocaleString()}
          sub={`${ch.active_campaigns ?? 0} active campaigns`}
        />
        <MetricCard
          label="Push notifications (7d)"
          value={(pu.outbox_week ?? 0).toLocaleString()}
          sub={`${pu.outbox_pending ?? 0} pending in outbox`}
        />
        <MetricCard
          label="Parallel sessions"
          value={(pa.sessions_total ?? 0).toLocaleString()}
          sub={`${pa.sessions_completed ?? 0} completed · ${pa.sessions_active ?? 0} active`}
        />
      </div>

      <AdminCard
        title="Sparks & redemptions"
        action={
          <Button
            variant="outline"
            onClick={() =>
              downloadCsv(
                "sparks-redemptions-pending.csv",
                ["user", "reward", "sparks", "status", "created"],
                pendingSparks.map((r) => [
                  r.username,
                  r.reward_id,
                  r.sparks_spent,
                  r.status,
                  r.created_at,
                ]),
              )
            }
          >
            Export pending CSV
          </Button>
        }
      >
        <StatGrid
          items={[
            { label: "Redemptions this week", value: s.redemptions_week ?? 0 },
            { label: "Pending recharge (sparks)", value: s.pending_recharge_sparks ?? 0 },
            { label: "Ad-free (Sparks perk)", value: s.ad_free_users ?? 0 },
            { label: "Season payout sparks", value: ch.season_payout_sparks ?? 0 },
          ]}
        />
        {pendingSparks.length ? (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs text-ink-muted">
                <tr>
                  <th className="px-2 py-2">User</th>
                  <th className="px-2 py-2">Reward</th>
                  <th className="px-2 py-2">Sparks</th>
                  <th className="px-2 py-2">When</th>
                </tr>
              </thead>
              <tbody>
                {pendingSparks.map((r) => (
                  <tr key={r.id} className="border-t border-black/[0.04]">
                    <td className="px-2 py-2">@{r.username ?? "—"}</td>
                    <td className="px-2 py-2">{r.reward_id}</td>
                    <td className="px-2 py-2 tabular-nums">{r.sparks_spent}</td>
                    <td className="px-2 py-2 text-xs text-ink-muted">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-3 text-sm text-ink-muted">No pending Sparks redemptions.</p>
        )}
      </AdminCard>

      <div className="grid gap-4 lg:grid-cols-2">
        <AdminCard title="Creator economy (platform)">
          <StatGrid
            items={[
              { label: "Tips (coins)", value: formatCoins(cr.tips_coins_total) },
              { label: "Collectible unlocks", value: formatCoins(cr.unlock_coins_total) },
              { label: "Creator Pro subs (coins)", value: formatCoins(cr.subscription_coins_total) },
              { label: "Feed impressions", value: cr.signal_impressions ?? 0 },
              { label: "Public creators", value: cr.public_creators ?? 0 },
              { label: "Season payouts (users)", value: ch.season_payout_users ?? 0 },
            ]}
          />
        </AdminCard>

        <AdminCard title="Push & devices">
          <StatGrid
            items={[
              { label: "FCM tokens", value: pu.device_tokens ?? 0 },
              { label: "Users with devices", value: pu.device_users ?? 0 },
              { label: "Active sessions (7d)", value: pu.sessions_active_7d ?? 0 },
              { label: "Outbox (24h)", value: pu.outbox_24h ?? 0 },
            ]}
          />
        </AdminCard>

        <AdminCard title="Parallel & Squad">
          <StatGrid
            items={[
              { label: "Sessions this week", value: pa.sessions_week ?? 0 },
              { label: "Captures", value: pa.captures_total ?? 0 },
              { label: "Squad groups", value: pa.squad_groups ?? 0 },
              { label: "Squad parallel runs", value: pa.squad_parallel_sessions ?? 0 },
            ]}
          />
        </AdminCard>

        <AdminCard title="Growth & invites">
          <StatGrid
            items={[
              { label: "Contact graph edges", value: gr.contact_edges ?? 0 },
              { label: "Users with contacts", value: gr.users_with_contacts ?? 0 },
              { label: "Phone sync users", value: gr.phone_sync_users ?? 0 },
              { label: "Invite outreach", value: gr.invite_outreach ?? 0 },
              { label: "Referrals credited", value: gr.referrals_credited ?? 0 },
              { label: "Referred signups", value: gr.referred_signups ?? 0 },
            ]}
          />
        </AdminCard>
      </div>

      <AdminCard title="AdMob (proxy metrics)">
        <StatGrid
          items={[
            { label: "Ads enabled (config)", value: ad.config_enabled ? "Yes" : "No" },
            { label: "Est. ad-free users", value: ad.ad_free_users_estimate ?? 0 },
          ]}
        />
        <p className="mt-3 text-xs text-ink-muted">
          Actual AdMob ₹ revenue requires Google&apos;s reporting API — not connected yet.
        </p>
      </AdminCard>

      <AdminCard title="Admin accounts">
        <ul className="space-y-2 text-sm">
          {admins.map((a) => (
            <li key={a.email ?? a.username ?? ""} className="flex justify-between gap-2">
              <span>@{a.username ?? "—"}</span>
              <span className="text-ink-muted">{a.email}</span>
            </li>
          ))}
        </ul>
      </AdminCard>

      <AdminCard title="Still requires external integration">
        <ul className="grid gap-2 sm:grid-cols-2">
          {ADMIN_NOT_IMPLEMENTED.map((item) => (
            <li
              key={item.area}
              className="rounded-xl border border-amber-200/80 bg-amber-50/80 px-3 py-2 text-sm"
            >
              <div className="font-medium text-amber-950">{item.area}</div>
              <div className="text-xs text-amber-900/80">{item.detail}</div>
            </li>
          ))}
        </ul>
      </AdminCard>
    </div>
  );
}
